/**
 * sealedStore.js — store LOCALE delle conversazioni sealed (v2.2).
 *
 * Il sealed-sender non ha chat-doc lato server (il server non sa chi-con-chi), e
 * il ratchet è forward-secret (una busta si decifra UNA volta sola). Quindi il
 * testo decifrato vive SOLO qui, su questo device, in IndexedDB.
 *
 * Namespaced per identità attiva (riusa getFsIdentity di fsStore): le conversazioni
 * sealed dell'identità Tor sono isolate da quelle di un'altra identità.
 */

import { getFsIdentity } from './fsStore';

const DB_NAME = 'nullum-sealed';
const DB_VER = 1;
const ST = 'convos';
const MAX_MSGS = 1000; // tetto per conversazione

// ── Effimero: i messaggi sealed (testo in chiaro SOLO locale) scadono e vengono
// CANCELLATI dopo un TTL, coerente con l'ethos di Nullum. Default 24h. 0 = mai.
const TTL_KEY = 'nullum.sealedTtl.v1';
const TTL_DEFAULT = 24 * 3600 * 1000;
export function getSealedTTL() {
  try { const v = localStorage.getItem(TTL_KEY); return v == null ? TTL_DEFAULT : Math.max(0, parseInt(v, 10) || 0); }
  catch { return TTL_DEFAULT; }
}
export function setSealedTTL(ms) {
  try { localStorage.setItem(TTL_KEY, String(Math.max(0, ms | 0))); } catch {}
}
const expOf = (ts) => { const ttl = getSealedTTL(); return ttl > 0 ? ts + ttl : 0; };
/**
 * Effimero PARI ai messaggi normali: la durata è scelta dal MITTENTE (`stt`,
 * secondi; -1 = ∞) e la scadenza parte dalla PRIMA lettura (`viewedAt`); i MIEI
 * messaggi partono dall'invio. Se `stt` manca (msg legacy) vale il vecchio TTL
 * locale (`exp`, 24h). Rileggere NON riavvia il timer.
 */
export function isExpiredSealed(m, now) {
  if (m.stt == null) return !!m.exp && m.exp <= now; // legacy: solo backstop 24h
  if (m.stt < 0) return false;                        // ∞
  if (!m.viewedAt) return false;                      // non ancora letto → non parte
  if (m.stt === 0) return m.viewedAt <= now;          // zero-persist (sparisce alla lettura)
  return m.viewedAt + m.stt * 1000 <= now;
}
/** Rimuove i messaggi scaduti da una conversazione. Ritorna [convoPulita, cambiata]. */
function prune(convo, now) {
  const before = convo.messages.length;
  convo.messages = convo.messages.filter((m) => !isExpiredSealed(m, now));
  return [convo, convo.messages.length !== before];
}

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ST)) db.createObjectStore(ST);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbGet(key) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const r = db.transaction(ST, 'readonly').objectStore(ST).get(key);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function idbPut(key, val) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const t = db.transaction(ST, 'readwrite');
    t.objectStore(ST).put(val, key);
    t.oncomplete = () => res(true);
    t.onerror = () => rej(t.error);
  });
}
async function idbAllKeys() {
  const db = await openDB();
  return new Promise((res, rej) => {
    const r = db.transaction(ST, 'readonly').objectStore(ST).getAllKeys();
    r.onsuccess = () => res(r.result || []);
    r.onerror = () => rej(r.error);
  });
}
async function idbDel(key) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const t = db.transaction(ST, 'readwrite');
    t.objectStore(ST).delete(key);
    t.oncomplete = () => res(true);
    t.onerror = () => rej(t.error);
  });
}

const idP = () => getFsIdentity() || '_';
const peerKey = (peerSigil) => `${idP()}::${String(peerSigil || '').toLowerCase().replace(/^@/, '')}`;

/** Aggiunge un messaggio alla conversazione col peer. Dedup per id.
 *  `stt` = TTL scelto dal mittente (secondi; -1 = ∞; null/undefined = legacy 24h). */
export async function addSealedMessage(peerSigil, { id, mine, text, ts, stt }) {
  const key = peerKey(peerSigil);
  const cur = (await idbGet(key)) || { peerSigil, messages: [], lastTs: 0 };
  if (id && cur.messages.some((m) => m.id === id)) return cur; // già presente
  cur.peerSigil = peerSigil; // preserva il case originale
  const sttN = (stt == null || !Number.isFinite(stt)) ? null : stt;
  cur.messages.push({
    id: id || `l_${ts}_${Math.floor(ts % 100000)}`,
    mine: !!mine, text, ts,
    exp: expOf(ts),               // backstop 24h (solo per i legacy senza stt)
    stt: sttN,                    // durata scelta dal mittente
    viewedAt: mine ? ts : null,   // i MIEI partono dall'invio; i ricevuti dalla lettura
  });
  cur.messages.sort((a, b) => a.ts - b.ts);
  if (cur.messages.length > MAX_MSGS) cur.messages = cur.messages.slice(-MAX_MSGS);
  cur.lastTs = cur.messages[cur.messages.length - 1]?.ts || ts;
  await idbPut(key, cur);
  return cur;
}

/** Ancora la scadenza alla PRIMA lettura di uno o più messaggi RICEVUTi (non
 *  riavvia se già letto). Chiamata da SealedChatScreen quando i messaggi sono a video. */
export async function markSealedViewed(peerSigil, ids, at = Date.now()) {
  const list = Array.isArray(ids) ? ids : [ids];
  if (!list.length) return false;
  const key = peerKey(peerSigil);
  const cur = await idbGet(key);
  if (!cur?.messages) return false;
  let changed = false;
  for (const m of cur.messages) {
    if (!m.mine && m.viewedAt == null && list.includes(m.id)) { m.viewedAt = at; changed = true; }
  }
  if (changed) await idbPut(key, cur);
  return changed;
}

function convoKeys() {
  const prefix = `${idP()}::`;
  return idbAllKeys().then((ks) => ks.filter((k) => typeof k === 'string' && k.startsWith(prefix) && !k.endsWith('::__fails')));
}

/** Conversazioni sealed da MOSTRARE: scaduti cancellati, vuote nascoste, recenti in testa. */
export async function listSealedConvos() {
  const now = Date.now();
  const out = [];
  for (const k of await convoKeys()) {
    const c = await idbGet(k);
    if (!c || !Array.isArray(c.messages)) continue;
    const [pruned, changed] = prune(c, now);
    if (changed) await idbPut(k, pruned);
    if (pruned.messages.length > 0) out.push(pruned);
  }
  out.sort((a, b) => (b.lastTs || 0) - (a.lastTs || 0));
  return out;
}

/** Sigil dei peer con cui ho una conversazione sealed (per il ROUTING dei ratchet):
 *  include anche le conversazioni i cui messaggi sono scaduti — la SESSIONE resta. */
export async function listSealedPeers() {
  const peers = [];
  for (const k of await convoKeys()) { const c = await idbGet(k); if (c?.peerSigil) peers.push(c.peerSigil); }
  return peers;
}

export async function getSealedConvo(peerSigil) {
  const c = (await idbGet(peerKey(peerSigil))) || { peerSigil, messages: [], lastTs: 0 };
  const [pruned, changed] = prune(c, Date.now());
  if (changed) await idbPut(peerKey(peerSigil), pruned);
  return pruned;
}

export async function deleteSealedConvo(peerSigil) {
  await idbDel(peerKey(peerSigil));
}

// ── Drenaggio buste non-decifrabili (anti-accumulo / anti-DoS) ────────────────
// Conta i tentativi di decifratura falliti per id-busta; oltre un budget la busta
// va ACK-cancellata (spazzatura o permanentemente non-decifrabile), così non
// resta in testa alla coda di fetch bloccando i messaggi veri.
const FAILS_KEY = () => `${idP()}::__fails`;
export async function bumpSealedFail(id) {
  const key = FAILS_KEY();
  const m = (await idbGet(key)) || {};
  m[id] = (m[id] || 0) + 1;
  await idbPut(key, m);
  return m[id];
}
export async function clearSealedFails(ids) {
  if (!ids?.length) return;
  const key = FAILS_KEY();
  const m = (await idbGet(key)) || {};
  let changed = false;
  for (const id of ids) if (id in m) { delete m[id]; changed = true; }
  if (changed) await idbPut(key, m);
}
